import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscribeToAuthChanges } from '../utils/authUtils.js';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context. Kept colocated with the provider on
// purpose — it has 30+ consumers, so splitting it out for fast-refresh's sake
// would churn far more than the dev-only HMR benefit is worth.
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps your app and makes auth object available to any
// child component that calls useAuth().
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Function to get the current user's ID token
  const getIdToken = useCallback(async () => {
    if (!currentUser) {
      // This case should ideally be handled by routing/UI before an API call is made
      // Or, the API service itself can check for currentUser before calling getIdToken
      console.error("getIdToken called when no user is logged in.");
      return null;
    }
    try {
      return await currentUser.getIdToken();
    } catch (error) {
      console.error("Error getting ID token:", error);
      // Potentially handle token refresh errors here or re-authenticate
      // For now, re-throwing or returning null might be options
      setError('Failed to get authentication token. Please try logging out and in again.');
      throw error; // Or return null, depending on how API service handles it
    }
  }, [currentUser]);

  // Set up auth state listener on mount
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setCurrentUser(user);
      setDisplayName(user?.displayName || null);
      setLoading(false);
    });

    // Clean up subscription on unmount
    return unsubscribe;
  }, []);

  // Admin features removed

  const value = {
    currentUser,
    displayName: displayName || currentUser?.displayName || null,
    getIdToken,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
